import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, message, Select } from 'antd';
import { createSoilAnalysis, getLastSoilAnalysis } from '../../../Utils/analysis/soilAnalysis';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../../Shared/Header';
import backIcon from "../../../assets/icons/backIcon.svg";
import CustomDatePicker from '../../Shared/CustomDatePicker';

const { Option } = Select;

const CreateSoilAnalysis = () => {
  const [form] = Form.useForm();
  const { exploitationId } = useParams();
  const [loading, setLoading] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLastAnalysis = async () => {
      try {
        const lastAnalysis = (await getLastSoilAnalysis(exploitationId)).data;
        if (lastAnalysis) {
          setLastAnalysis(lastAnalysis);
          // Prefill the form with min and max values from the last analysis
          const prefillData = {};
          Object.keys(lastAnalysis).forEach(key => {
            if (key.endsWith('_min') || key.endsWith('_max')) {
              prefillData[key] = lastAnalysis[key];
            }
          });
          form.setFieldsValue(prefillData);
        }
      } catch (error) {
        console.error('Error fetching last soil analysis:', error);
        message.error('Failed to fetch last soil analysis data');
      }
    };

    fetchLastAnalysis();
  }, [exploitationId, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const formattedValues = {
        ...values,
        date_realisation: values.date_realisation.toISOString(),
        exploitation_id: exploitationId,
      };
      await createSoilAnalysis(formattedValues);
      form.resetFields();
      message.success('Analyse de sol créée avec succès');
      navigate(`/admin/exploitation/${exploitationId}/analysis/sol`);
    } catch (error) {
      console.error('Erreur lors de la création de l\'analyse de sol:', error);
      message.error('Échec de la création de l\'analyse de sol');
    } finally {
      setLoading(false);
    }
  };

  const duplicateLastAnalysis = () => {
    if (lastAnalysis) {
      const duplicatedData = { ...lastAnalysis };
      // Remove or reset fields that shouldn't be duplicated
      delete duplicatedData.id;
      duplicatedData.date_realisation = null;
      form.setFieldsValue(duplicatedData);
      message.success('Dernière analyse dupliquée');
    } else {
      message.warning('Aucune analyse précédente à dupliquer');
    }
  };

  const textureOptions = [
    'argile',
    'argile lourd',
    'argile limoneux',
    'argile sableux',
    'limon argileux',
    'limon argileux fin',
    'limon argilo-sableux',
    'limon',
    'limon fin',
    'limon tres fin',
    'limon sableux',
    'limon tres sableux',
    'sable limoneux',
    'sable'
  ];

  const granulometryParameters = [
    { key: 'texture', parameter: 'Texture' },
    { key: 'argile', parameter: 'Argile' },
    { key: 'sablefin', parameter: 'Sable fin' },
    { key: 'limonfin', parameter: 'Limon fin' },
    { key: 'limongrossier', parameter: 'Limon grossier' },
    { key: 'sablegrossier', parameter: 'Sable grossier' },
    { key: 'gravier', parameter: 'Gravier' },
    { key: 'humiditeeq', parameter: 'Humidité équivalente' },
  ];

  const chemicalParameters = [
    { key: 'etatHumique', parameter: 'Etat humique', isHeader: true },
    { key: 'matiereorganique', parameter: 'Matière organique (%)' },
    { key: 'azotetotal', parameter: 'Azote total (N) (g/kg)' },
    { key: 'rapportCN', parameter: 'Rapport C/N' },
    { key: 'statutAcidoBasique', parameter: 'Statut Acido-Basique', isHeader: true },
    { key: 'pheau', parameter: 'pH eau' },
    { key: 'phkci', parameter: 'pH KCl' },
    { key: 'calcairetotal', parameter: 'Calcaire total (CaCO₃) (%)' },
    { key: 'calcaireactif', parameter: 'Calcaire actif (CaCO₃) (%)' },
    { key: 'cao', parameter: 'CaO (mg/kg)' },
    { key: 'teneurphosphoresoluble', parameter: 'Teneur en phosphore soluble (P₂O₅) (mg/kg)' },
    { key: 'potasse', parameter: 'Potasse (K₂O) (mg/kg)' },
    { key: 'magnesie', parameter: 'Magnésie (MgO) (mg/kg)' },
    { key: 'oligoElements', parameter: 'Oligo-éléments', isHeader: true },
    { key: 'cuivre', parameter: 'Cuivre (Cu) (mg/kg)' },
    { key: 'manganese', parameter: 'Manganèse (Mn) (mg/kg)' },
    { key: 'fer', parameter: 'Fer (Fe) (mg/kg)' },
    { key: 'zinc', parameter: 'Zinc (Zn) (mg/kg)' },
    { key: 'boresoluble', parameter: 'Bore soluble (B) (mg/kg)' },
    { key: 'autresElements', parameter: 'Autres éléments', isHeader: true },
    { key: 'ec', parameter: 'Conductivité électrique (EC) (1/5 mS/cm)' },
    { key: 'sodium', parameter: 'Sodium (Na) (mg/kg)' },
    { key: 'cec', parameter: 'CEC (Capacité d\'Échange Cationique) (meq/100g)' },
  ];

  const aqueousExtractParameters = [
    { key: 'potassium', parameter: 'Potassium (K⁺)' },
    { key: 'sodium', parameter: 'Sodium (Na⁺)' },
    { key: 'magnesium', parameter: 'Magnésium (Mg²⁺)' },
    { key: 'calcium', parameter: 'Calcium (Ca²⁺)' },
    { key: 'ammonium', parameter: 'Ammonium (N-NH₄⁺)' },
    { key: 'nitrate', parameter: 'Nitrate (NO₃⁻)' },
    { key: 'phosphore', parameter: 'Phosphore (PO₄³⁻)' },
    { key: 'chlorures', parameter: 'Chlorures (Cl⁻)' },
    { key: 'sulfates', parameter: 'Sulfates (SO₄²⁻)' },
  ];

  return (
    <div className="bg-white h-full">
      <Header title={`
        Créer une analyse de sol
              `} />
      <div className="h-[calc(100%-100px)] p-6">
        <Link
          to={`/admin/exploitation/${exploitationId}/analysis/sol`}
          className="cursor-pointer flex pl-5 gap-2 mt-4"
        >
          <img src={backIcon} alt="" className="w-5" />{" "}
          <div className="font-semibold text-base text-indigo-600">
            {" "}
            Retour
          </div>
        </Link>
        <div className='max-w-[1000px] mx-auto'>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Nouvelle analyse de sol</h2>
            <Button onClick={duplicateLastAnalysis} type='secondary' className='text-md border-indigo-600 border-2 text-indigo-600 hover:bg-indigo-600 hover:text-white'>
              Dupliquer la dernière analyse
            </Button>
          </div>
          <Form
            form={form}
            name="createSoilAnalysis"
            onFinish={onFinish}
            layout="vertical"
            className='bg-white p-6 max-w-[1000px] rounded-lg shadow-md'
          >
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Form.Item name="date_realisation" label="Date de réalisation" rules={[{ required: true }]}>
                <CustomDatePicker />
              </Form.Item>
              <Form.Item name="laboratoire" label="Laboratoire">
                <Input />
              </Form.Item>
              <Form.Item name="echantillon" label="Échantillon">
                <Input />
              </Form.Item>
              <Form.Item name="vanne" label="Vanne">
                <Input />
              </Form.Item>
            </div>
            <h2 className="text-xl font-semibold mt-6 mb-4">Granulométrie</h2>
            <table className="w-full border-collapse border border-gray-300 mb-4">
              <thead>
                <tr>
                  <HeaderTab text={'Paramètre'} />
                  <HeaderTab text={'Horizon 1'} />
                  <HeaderTab text={'Horizon 2'} />
                </tr>
              </thead>
              <tbody>
                {granulometryParameters.map(param => (
                  <tr key={param.key}>
                    <td className="border border-gray-300 px-2 py-2">{param.parameter}</td>
                    <td className="border border-gray-300 px-2 py-2">
                      <Form.Item name={`${param.key}_h1`} noStyle>
                        {param.key === 'texture' ? (
                          <Select style={{ width: '100%' }}>
                            {textureOptions.map(option => (
                              <Option key={option} value={option}>{option}</Option>
                            ))}
                          </Select>
                        ) : (
                          <InputNumber step={0.01} addonAfter="%" />
                        )}
                      </Form.Item>
                    </td>
                    <td className="border border-gray-300 px-2 py-2">
                      <Form.Item name={`${param.key}_h2`} noStyle>
                        {param.key === 'texture' ? (
                          <Select style={{ width: '100%' }}>
                            {textureOptions.map(option => (
                              <Option key={option} value={option}>{option}</Option>
                            ))}
                          </Select>
                        ) : (
                          <InputNumber step={0.01} addonAfter="%" />
                        )}
                      </Form.Item>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h2 className="text-xl font-semibold mt-6 mb-4">Paramètres chimiques</h2>
            <table className="w-full border-collapse border border-gray-300 mb-4">
              <thead>
                <tr>
                  <th className="border border-gray-300 bg-gray-300 px-2 py-2" rowSpan="2">Paramètre</th>
                  <th className="border border-gray-300 bg-gray-300 px-2 py-2 text-lg " colSpan="3" >Horizon 1</th>
                  <th className="border border-gray-300 px-2 bg-gray-300 py-2 text-lg" colSpan="3" >Horizon 2</th>
                </tr>
                <tr>
                  <HeaderTab text={'Min'} />
                  <HeaderTab text={'Max'} />
                  <HeaderTab text={'Donnée'} />
                  <HeaderTab text={'Min'} />
                  <HeaderTab text={'Max'} />
                  <HeaderTab text={'Donnée'} />
                </tr>
              </thead>
              <tbody>
                {chemicalParameters.map(param => (
                  <tr key={param.key} className={param.isHeader ? 'bg-gray-200' : ''}>
                    <td className="border border-gray-300 px-2 py-2">{param.parameter}</td>
                    {param.isHeader ? (
                      <td className="border border-gray-300 px-2 py-2" colSpan="6"></td>
                    ) : (
                      <>
                        <td className="border border-gray-300 px-2 py-2">
                          <Form.Item name={`${param.key}_h1_min`} noStyle>
                            <InputNumber step={0.01} />
                          </Form.Item>
                        </td>
                        <td className="border border-gray-300 px-2 py-2">
                          <Form.Item name={`${param.key}_h1_max`} noStyle>
                            <InputNumber step={0.01} />
                          </Form.Item>
                        </td>
                        <td className="border border-gray-300 px-2 py-2">
                          <Form.Item name={`${param.key}_h1_data`} noStyle>
                            <InputNumber step={0.01} />
                          </Form.Item>
                        </td>
                        <td className="border border-gray-300 px-2 py-2">
                          <Form.Item name={`${param.key}_h2_min`} noStyle>
                            <InputNumber step={0.01} />
                          </Form.Item>
                        </td>
                        <td className="border border-gray-300 px-2 py-2">
                          <Form.Item name={`${param.key}_h2_max`} noStyle>
                            <InputNumber step={0.01} />
                          </Form.Item>
                        </td>
                        <td className="border border-gray-300 px-2 py-2">
                          <Form.Item name={`${param.key}_h2_data`} noStyle>
                            <InputNumber step={0.01} />
                          </Form.Item>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <h2 className="text-xl font-semibold mt-6 mb-4">Extrait aqueux</h2>
            <table className="w-full border-collapse border border-gray-300 mb-4">
              <thead>
                <tr>
                  <HeaderTab text={'Paramètre'} />
                  <HeaderTab text={'Min (1/5mg/100g)'} />
                  <HeaderTab text={'Max (1/5mg/100g)'} />
                  <HeaderTab text={'H1 (1/5mg/100g)'} />
                  <HeaderTab text={'H2 (1/5mg/100g)'} />
                </tr>
              </thead>
              <tbody>
                {aqueousExtractParameters.map(param => (
                  <tr key={param.key}>
                    <td className="border border-gray-300 px-2 py-2">{param.parameter}</td>
                    <td className="border border-gray-300 px-2 py-2">
                      <Form.Item name={`${param.key}_min`} noStyle>
                        <InputNumber step={0.01} />
                      </Form.Item>
                    </td>
                    <td className="border border-gray-300 px-2 py-2">
                      <Form.Item name={`${param.key}_max`} noStyle>
                        <InputNumber step={0.01} />
                      </Form.Item>
                    </td>
                    <td className="border border-gray-300 px-2 py-2">
                      <Form.Item name={`${param.key}_h1`} noStyle>
                        <InputNumber step={0.01} />
                      </Form.Item>
                    </td>
                    <td className="border border-gray-300 px-2 py-2">
                      <Form.Item name={`${param.key}_h2`} noStyle>
                        <InputNumber step={0.01} />
                      </Form.Item>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Form.Item className="mt-8 flex justify-end">
              <Button className='text-lg p-4' type="primary" htmlType="submit" loading={loading}>
                Créer l'Analyse de Sol
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default CreateSoilAnalysis;


const HeaderTab = ({ text }) => (
  <th className="border border-gray-300 bg-gray-300 px-2 py-2">{text}</th>
);